// LocalStorage-backed AuthStoragePort. Survives F5; cleared on logout
// (invariant W5). Handles SSR / missing-window the same way office-init.ts
// does — return null gracefully so tests + build don't crash.

import type { AuthStoragePort } from '@/domain/auth/ports'
import type { AuthSession } from '@/domain/auth/entity'

// Unique key under the addin namespace. If Office's iframe sandbox happens
// to share localStorage with the SaaS domain on some host (it shouldn't —
// taskpane.theplaidoyer.com vs theplaidoyer.com — but defense in depth),
// the prefix prevents accidental collision with FE keys.
const STORAGE_KEY = 'theplaidoyer.addin.auth'

/** Schema-checked decoder — refuses partial payloads from older versions.
 *  Today there's only v1; if we ever rename a field, bump the version
 *  marker and add a migration here. */
function isValidSession(value: unknown): value is AuthSession {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  return (
    typeof v.accessToken === 'string' &&
    typeof v.userId === 'string' &&
    typeof v.email === 'string' &&
    typeof v.isAdmin === 'boolean' &&
    typeof v.isOwner === 'boolean'
  )
}

export function createAuthStorage(): AuthStoragePort {
  return {
    read(): AuthSession | null {
      if (typeof window === 'undefined') return null
      try {
        const raw = window.localStorage?.getItem(STORAGE_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw) as unknown
        if (!isValidSession(parsed)) {
          // Stale shape — purge so the LoginScreen surfaces cleanly.
          window.localStorage?.removeItem(STORAGE_KEY)
          return null
        }
        return parsed
      } catch {
        return null
      }
    },
    write(session: AuthSession): void {
      if (typeof window === 'undefined') return
      try {
        window.localStorage?.setItem(STORAGE_KEY, JSON.stringify(session))
      } catch {
        // localStorage may be disabled (Office sandbox edge case on
        // some Word for Mac builds). The in-memory Zustand store still
        // carries the session for the lifetime of the taskpane.
      }
    },
    clear(): void {
      if (typeof window === 'undefined') return
      try {
        window.localStorage?.removeItem(STORAGE_KEY)
      } catch {
        // ignore — same rationale as write()
      }
    },
  }
}

// Exported for the auth-storage test only.
export const AUTH_STORAGE_KEY = STORAGE_KEY
